/* eslint-disable no-empty-pattern */
import HttpRequest from "@/util/HttpRequest";

// API 요청 진행 상태를 추적하는 Map
const pendingRequests = new Map();

// 캐시 만료 시간 (10분 = 600,000ms)
const CACHE_DURATION = 300; // 10 * 60 * 1000;

// localStorage 키 접두사
const CACHE_PREFIX = 'maot_summary_cache_';

// 요청 키를 생성하는 헬퍼 함수
function createRequestKey(actionName, params) {
  return `${actionName}_${JSON.stringify(params)}`;
}

// 캐시가 유효한지 확인하는 함수
function isCacheValid(timestamp) {
  return Date.now() - timestamp < CACHE_DURATION;
}

// localStorage에서 캐시 데이터 조회
function getCacheFromStorage(requestKey) {
  try {
    const cacheKey = CACHE_PREFIX + requestKey;
    const cachedItem = localStorage.getItem(cacheKey);
    if (cachedItem) {
      return JSON.parse(cachedItem);
    }
  } catch (error) {
    console.warn('캐시 데이터 조회 실패:', error);
  }
  return null;
}

// localStorage에 캐시 데이터 저장
function setCacheToStorage(requestKey, data) {
  try {
    const cacheKey = CACHE_PREFIX + requestKey;
    const cacheItem = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
  } catch (error) {
    console.warn('캐시 데이터 저장 실패:', error);
  }
}

// 만료된 캐시 삭제
function removeCacheFromStorage(requestKey) {
  try {
    const cacheKey = CACHE_PREFIX + requestKey;
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.warn('캐시 데이터 삭제 실패:', error);
  }
}

// 진행 중인 요청이 있는지 확인하고, 있으면 그 Promise를 반환, 없으면 새로운 요청을 시작
function handleRequest(actionName, params, requestFunction) {
  const requestKey = createRequestKey(actionName, params);

  // localStorage에서 캐시된 데이터 확인
  const cachedItem = getCacheFromStorage(requestKey);
  if (cachedItem && isCacheValid(cachedItem.timestamp)) {
    // 캐시된 데이터를 Promise로 반환
    return Promise.resolve(cachedItem.data);
  } else if (cachedItem) {
    // 만료된 캐시 제거
    removeCacheFromStorage(requestKey);
  }

  // 이미 진행 중인 동일한 요청이 있으면 그 Promise를 반환
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  // 새로운 요청 시작
  const promise = requestFunction()
    .then((response) => {
      // 응답 데이터를 localStorage에 캐시 저장
      setCacheToStorage(requestKey, response);
      return response;
    })
    .finally(() => {
      // 요청 완료 후 진행 중인 요청 Map에서 제거
      pendingRequests.delete(requestKey);
    });

  // 진행 중인 요청으로 등록
  pendingRequests.set(requestKey, promise);

  return promise;
}

function createSummaryMonthly(response, groupName) {
  const monthly = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const projList = Object.keys(response);
  const countMonthly = [];
  const countProjects = {};
  let countTotal = 0;

  const sumMonthly = [];
  const sumProjects = {};
  let sumTotal = 0;

  for (const projName of projList) {
    if (!countProjects[projName]) countProjects[projName] = {
      total: 0,
      monthly: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      child: {},
    };
    if (!sumProjects[projName]) sumProjects[projName] = {
      total: 0,
      monthly: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      child: {},
    };

    const data = response[projName];
    if (data) {
      const companys = Object.keys(data);
      for (const company of companys) {
        if (!countProjects[projName].child[company]) countProjects[projName].child[company] = {
          total: 0,
          monthly: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
          child: {},
        };
        if (!sumProjects[projName].child[company]) sumProjects[projName].child[company] = {
          total: 0,
          monthly: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
          child: {},
        };

        for (const month of monthly) {
          if (!countMonthly[month - 1]) countMonthly[month - 1] = 0;
          if (!countProjects[projName].monthly[month - 1]) countProjects[projName].monthly[month - 1] = 0;
          if (!countProjects[projName].child[company].monthly[month - 1]) countProjects[projName].child[company].monthly[month - 1] = 0;
          if (data[company].count[month] && data[company].count[month][groupName]) {
            const keys = Object.keys(data[company].count[month][groupName]);
            for (const key of keys) {
              if (!countProjects[projName].child[company].child[key]) countProjects[projName].child[company].child[key] = {
                total: 0,
                monthly: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
              };
              if (!countProjects[projName].child[company].child[key].monthly[month - 1]) countProjects[projName].child[company].child[key].monthly[month - 1] = 0;
              if (data[company].count[month][groupName][key]) {
                const _count = parseInt(`${data[company].count[month][groupName][key]}`);
                countMonthly[month - 1] += _count;
                countTotal += _count;
                countProjects[projName].monthly[month - 1] += _count;
                countProjects[projName].total += _count;
                countProjects[projName].child[company].monthly[month - 1] += _count;
                countProjects[projName].child[company].total += _count;
                countProjects[projName].child[company].child[key].monthly[month - 1] += _count;
                countProjects[projName].child[company].child[key].total += _count;
              }
            }
          }

          if (!sumMonthly[month - 1]) sumMonthly[month - 1] = 0;
          if (!sumProjects[projName].monthly[month - 1]) sumProjects[projName].monthly[month - 1] = 0;
          if (!sumProjects[projName].child[company].monthly[month - 1]) sumProjects[projName].child[company].monthly[month - 1] = 0;
          if (data[company].sum[month] && data[company].sum[month][groupName]) {
            const keys = Object.keys(data[company].sum[month][groupName]);
            for (const key of keys) {
              if (!sumProjects[projName].child[company].child[key]) sumProjects[projName].child[company].child[key] = {
                total: 0,
                monthly: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
              };
              if (!sumProjects[projName].child[company].child[key].monthly[month - 1]) sumProjects[projName].child[company].child[key].monthly[month - 1] = 0;
              if (data[company].sum[month][groupName][key]) {
                const _sum = parseInt(`${data[company].sum[month][groupName][key]}`);
                sumMonthly[month - 1] += _sum;
                sumTotal += _sum;
                sumProjects[projName].monthly[month - 1] += _sum;
                sumProjects[projName].total += _sum;
                sumProjects[projName].child[company].monthly[month - 1] += _sum;
                sumProjects[projName].child[company].total += _sum;
                sumProjects[projName].child[company].child[key].monthly[month - 1] += _sum;
                sumProjects[projName].child[company].child[key].total += _sum;
              }
            }
          }
        } // monthly
      } // company
    }
  }

  return {
    sum: {
      total: sumTotal,
      monthly: sumMonthly,
      child: sumProjects,
    },
    count: {
      total: countTotal,
      monthly: countMonthly,
      child: countProjects,
    },
  };
}

function createSummaryItems(response, groupName) {
  const monthly = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const projList = Object.keys(response);
  const countItems = {};
  const countProjects = {};
  const countMonthly = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}];
  let countTotal = 0;

  const sumItems = {};
  const sumProjects = {};
  const sumMonthly = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}];
  let sumTotal = 0;

  for (const projName of projList) {
    if (!countProjects[projName]) countProjects[projName] = {
      total: 0,
      items: {},
      monthly: [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}],
      child: {},
    };
    if (!sumProjects[projName]) sumProjects[projName] = {
      total: 0,
      items: {},
      monthly: [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}],
      child: {},
    };

    const data = response[projName];
    if (data) {
      const companys = Object.keys(data);
      for (const company of companys) {
        if (!countProjects[projName].child[company]) countProjects[projName].child[company] = {
          total: 0,
          items: {},
          monthly: [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}],
          child: {},
        };
        if (!sumProjects[projName].child[company]) sumProjects[projName].child[company] = {
          total: 0,
          items: {},
          monthly: [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}],
          child: {},
        };

        for (const month of monthly) {
          if (!countProjects[projName].child[company].child[month]) countProjects[projName].child[company].child[month] = {
            total: 0,
            items: {},
            monthly: [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}],
          };
          if (!sumProjects[projName].child[company].child[month]) sumProjects[projName].child[company].child[month] = {
            total: 0,
            items: {},
            monthly: [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}],
          };

          if (data[company].count[month][groupName]) {
            const itemObj = data[company].count[month][groupName];
            const items = Object.keys(itemObj);

            for (const item of items) {
              if (!countItems[item]) countItems[item] = 0;
              if (!countProjects[projName].items[item]) countProjects[projName].items[item] = 0;
              if (!countProjects[projName].child[company].items[item]) countProjects[projName].child[company].items[item] = 0;
              if (!countProjects[projName].child[company].child[month].items[item]) countProjects[projName].child[company].child[month].items[item] = 0;
              if (!countMonthly[month - 1][item]) countMonthly[month - 1][item] = 0;
              if (!countProjects[projName].monthly[month - 1][item]) countProjects[projName].monthly[month - 1][item] = 0;
              if (!countProjects[projName].child[company].monthly[month - 1][item]) countProjects[projName].child[company].monthly[month - 1][item] = 0;

              countTotal += itemObj[item];
              countItems[item] += itemObj[item];
              countProjects[projName].total += itemObj[item];
              countProjects[projName].items[item] += itemObj[item];
              countProjects[projName].child[company].total += itemObj[item];
              countProjects[projName].child[company].items[item] += itemObj[item];
              countProjects[projName].child[company].child[month].total += itemObj[item];
              countProjects[projName].child[company].child[month].items[item] += itemObj[item];

              countMonthly[month - 1][item] += itemObj[item];
              countProjects[projName].monthly[month - 1][item] += itemObj[item];
              countProjects[projName].child[company].monthly[month - 1][item] += itemObj[item];
            }
          }

          if (data[company].sum[month][groupName]) {
            const itemObj = data[company].sum[month][groupName];
            const items = Object.keys(itemObj);

            for (const item of items) {
              if (!sumItems[item]) sumItems[item] = 0;
              if (!sumProjects[projName].items[item]) sumProjects[projName].items[item] = 0;
              if (!sumProjects[projName].child[company].items[item]) sumProjects[projName].child[company].items[item] = 0;
              if (!sumProjects[projName].child[company].child[month].items[item]) sumProjects[projName].child[company].child[month].items[item] = 0;
              if (!sumMonthly[month - 1][item]) sumMonthly[month - 1][item] = 0;
              if (!sumProjects[projName].monthly[month - 1][item]) sumProjects[projName].monthly[month - 1][item] = 0;
              if (!sumProjects[projName].child[company].monthly[month - 1][item]) sumProjects[projName].child[company].monthly[month - 1][item] = 0;

              sumTotal += itemObj[item];
              sumItems[item] += itemObj[item];
              sumProjects[projName].total += itemObj[item];
              sumProjects[projName].items[item] += itemObj[item];
              sumProjects[projName].child[company].total += itemObj[item];
              sumProjects[projName].child[company].items[item] += itemObj[item];
              sumProjects[projName].child[company].child[month].total += itemObj[item];
              sumProjects[projName].child[company].child[month].items[item] += itemObj[item];

              sumMonthly[month - 1][item] += itemObj[item];
              sumProjects[projName].monthly[month - 1][item] += itemObj[item];
              sumProjects[projName].child[company].monthly[month - 1][item] += itemObj[item];
            }
          }
        }
      } // company
    }
  }

  return {
    sum: {
      total: sumTotal,
      items: sumItems,
      monthly: sumMonthly,
      child: sumProjects,
    },
    count: {
      total: countTotal,
      items: countItems,
      monthly: countMonthly,
      child: countProjects,
    },
  };
}

export default {
  namespaced: true,
  state: {
    list: [],
    summaryMonthly: {},
    summaryItems: {},
  },
  mutations: {
    LOAD_SUMMARY_FIELD(state, data) {
      state.list = data;
    },
    LOAD_SUMMARY_FIELD_COMPANY(state, data) {
      state.list = data;
    },
    SET_LV1_CONTENT(state, data) {
      state.list = data;
    },
    SET_LV1_CONTENT_MONTHLY(state, data) {
      state.list = data;
    },
    SET_LV2_CONTENT_MONTHLY(state, data) {
      state.list = data;
    },
    SET_LV2_CONTENT(state, data) {
      state.list = data;
    },
    SET_LV2_GROUP(state, data) {
      state.list = data;
    },
    SET_SUMMARY(state, data) {
      state.list = data;
    },
    SET_POST_EACH_FIELDS(state, data) {
      state.list = data;
    },
    SET_SUMMARY_MONTHLY(state, data) {
      state.summaryMonthly = data;
    },
    SET_SUMMARY_ITEMS(state, data) {
      state.summaryItems = data;
    },
  },
  actions: {
    async LOAD_SUMMARY_FIELD({ commit }, params) {
      return handleRequest('LOAD_SUMMARY_FIELD', params, () => {
        const option = {
          params: {
            beginAt: params.beginAt,
            endAt: params.endAt,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/field-type", option)
            .then((response) => {
              commit("LOAD_SUMMARY_FIELD", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },
    async LOAD_SUMMARY_FIELD_COMPANY({ commit }, params) {
      return handleRequest('LOAD_SUMMARY_FIELD_COMPANY', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/field/company", option)
            .then((response) => {
              commit("LOAD_SUMMARY_FIELD_COMPANY", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    /**
     * 107, 154, 155 전용 API
     * 사업장별로 예산 문서를 불러온다
     *
     * @param commit
     * @param params
     * @returns {Promise<unknown>}
     * @constructor
     */
    async LOAD_POST_EACH_FIELDS({ commit }, params) {
      return handleRequest('LOAD_POST_EACH_FIELDS', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            year: params.year,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/board/post/${params.boardId}/each-field`, option)
            .then((response) => {
              commit("SET_POST_EACH_FIELDS", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_02({}, params) {
      return handleRequest('LOAD_DONUT_02', params, () => {
        const option = {
          params: {
            projectIds: params.projectIds,
            depth: params.depth,
            fromYear: params.fromYear,
            fieldIds: params.fieldIds,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/02", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },
    async LOAD_DONUT_02_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_02_DETAIL', params, () => {
        const option = {
          params: {
            projectIds: params.projectIds,
            depth: params.depth,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/02/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_06_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_06_DETAIL', params, () => {
        const option = {
          params: {
            boardIds: params.boardIds,
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
            fieldIds: params.fieldIds,
            currentYear: params.currentYear,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/06/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_08_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_08_DETAIL', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            boardIds: params.boardIds,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
            fieldIds: params.fieldIds,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/08/detail/158", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_09_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_09_DETAIL', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            boardIds: params.boardIds,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
            fieldIds: params.fieldIds,
            currentYear: params.currentYear,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/09/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_12_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_12_DETAIL', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
            fieldIds: params.fieldIds,
            boardIds: params.boardIds,
            currentYear: params.currentYear,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/12/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_15_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_15_DETAIL', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            fieldCodes: params.fieldCodes,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
            currentYear: params.currentYear,
            boardIds: params.boardIds,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/15/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_16_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_16_DETAIL', params, () => {
        const option = {
          params: {
            currentYear: params.currentYear,
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
            boardIds: params.boardids,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/16/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_27_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_27_DETAIL', params, () => {
        const option = {
          params: {
            boardIds: params.boardIds,
            currentYear: params.currentYear,
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/27/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },
    async LOAD_DONUT_28_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_28_DETAIL', params, () => {
        const option = {
          params: {
            currentYear: params.currentYear,
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/28/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },
    async LOAD_DONUT_30_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_30_DETAIL', params, () => {
        const option = {
          params: {
            currentYear: params.currentYear,
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/30/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },
    async LOAD_DONUT_34({}, params) {
      return handleRequest('LOAD_DONUT_34', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            fromYear: params.fromYear,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/34", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },
    async LOAD_DONUT_34_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_34_DETAIL', params, () => {
        const option = {
          params: {
            boardIds: params.boardIds,
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            currentYear: params.currentYear,
            depth: params.depth,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/34/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_35({}, params) {
      return handleRequest('LOAD_DONUT_35', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            projectIds: params.projectIds,
            depth: params.depth,
            fieldCode: params.fieldCode,
            fromYear: params.fromYear,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/35", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },
    async LOAD_DONUT_35_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_35_DETAIL', params, () => {
        const option = {
          params: {
            projectIds: params.projectIds,
            depth: params.depth,
            fieldCode: params.fieldCode,
            currentYear: params.currentYear,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/35/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_38_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_38_DETAIL', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
            currentYear: params.currentYear,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/38/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_39_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_39_DETAIL', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            boardIds: params.boardIds,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
            currentYear: params.currentYear,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/39/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },
    async LOAD_DONUT_87_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_87_DETAIL', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            boardIds: params.boardIds,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
            currentYear: params.currentYear,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/87/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_DONUT_89_DETAIL({}, params) {
      return handleRequest('LOAD_DONUT_89_DETAIL', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            fieldCode: params.fieldCode,
            boardIds: params.boardIds,
            depth: params.depth,
            excludeProjectIds: params.excludeProjectIds,
            currentYear: params.currentYear,

            // 날짜가 저장된 필드
            // post_content 내에서 JSON_EXTRACT를 사용해서 검색할 경우, "$."을 포함해야 한다 ($.body.stdInfo.headerDate)
            dateField: params.dateField,
            // 데이터 수를 카운트 할 필드
            // body.inputA_5
            countFields: params.countFields,
            // 데이터 합계를 구할 필드
            // body.inputA_6
            sumFields: params.sumFields,
          },
        };
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/donut/89/detail", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async FIRA_LOAD_SUMMARY({ commit }, params) {
      return handleRequest('FIRA_LOAD_SUMMARY', params, () => {
        const option = {
          params: {
            boardId: params.boardId,
            boardIds: params.boardIds,
            dateField: params.dateField,
            beginAt: params.beginAt,
            endAt: params.endAt,
            dateField2: params.dateField2,
            beginAt2: params.beginAt2,
            endAt2: params.endAt2,
            projectIds: params.projectIds,
            companyIds: params.companyIds,
            excludeCompanyIds: params.excludeCompanyIds,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary", option)
            .then((response) => {
              commit("SET_SUMMARY", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async FIRA_LOAD_SUMMARY_MONTHLY({ commit }, params) {
      return handleRequest('FIRA_LOAD_SUMMARY_MONTHLY', params, () => {
        const option = {
          params: {
            boardId: params.boardId,
            beginYear: params.beginYear,
            endYear: params.endYear,
            projectId: params.projectId,
            companyIds: params.companyIds,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/monthly", option)
            .then((response) => {
              commit("SET_SUMMARY", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async FIRA_LOAD_SUMMARY_DETAIL({ commit }, params) {
      return handleRequest('FIRA_LOAD_SUMMARY_DETAIL', params, () => {
        const option = {
          params: {
            boardId: params.boardId,
            beginAt: params.beginAt,
            endAt: params.endAt,
            projectId: params.projectId,
            dateField: params.dateField,
            groupByProject: false,
            groupByMonthly: true,
            groupByCompany: 1,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/detail", option)
            .then((response) => {
              commit("SET_SUMMARY", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    // lv1 권한 데이터 가져오기
    async LOAD_LV1_CONTENT({ commit }, params) {
      return handleRequest('LOAD_LV1_CONTENT', params, () => {
        const option = {
          params: {
            boardId: params.boardId,
            beginAt: params.beginAt,
            endAt: params.endAt,
            keyword: params.keyword,
            dateField: params.dateField,
            order: params.order,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/lv1/content", option)
            .then((response) => {
              commit("SET_LV1_CONTENT", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    // lv1 권한 데이터 연도별 / 월별 게시글 합계
    async LOAD_LV1_CONTENT_MONTHLY({ commit }, params) {
      return handleRequest('LOAD_LV1_CONTENT_MONTHLY', params, () => {
        const option = {
          params: {
            boardId: params.boardId,
            beginYear: params.beginYear,
            endYear: params.endYear,
            keyword: params.keyword,
            dateField: params.dateField,
            order: params.order,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/lv1/content/monthly", option)
            .then((response) => {
              commit("SET_LV1_CONTENT_MONTHLY", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    // lv2 권한 데이터 연도별 / 월별 게시글 합계
    async LOAD_LV2_CONTENT_MONTHLY({ commit }, params) {
      return handleRequest('LOAD_LV2_CONTENT_MONTHLY', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            boardId: params.boardId,
            beginYear: params.beginYear,
            endYear: params.endYear,
            keyword: params.keyword,
            dateField: params.dateField,
            order: params.order,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/lv2/content/monthly", option)
            .then((response) => {
              commit("SET_LV2_CONTENT_MONTHLY", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    // lv2 권한 데이터 가져오기
    async LOAD_LV2_CONTENT({ commit }, params) {
      return handleRequest('LOAD_LV2_CONTENT', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            boardId: params.boardId,
            beginAt: params.beginAt,
            endAt: params.endAt,
            keyword: params.keyword,
            dateField: params.dateField,
            order: params.order,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/lv2/content", option)
            .then((response) => {
              commit("SET_LV2_CONTENT", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    // lv2 권한 데이터 가져오기
    async LOAD_LV2_GROUP({ commit }, params) {
      return handleRequest('LOAD_LV2_GROUP', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            boardId: params.boardId,
            beginAt: params.beginAt,
            endAt: params.endAt,
            keyword: params.keyword,
            dateField: params.dateField,
            order: params.order,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/lv2/group", option)
            .then((response) => {
              commit("SET_LV2_GROUP", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_SUMMARY({ commit }, params) {
      return handleRequest('LOAD_SUMMARY', params, () => {
        const option = {
          params: {
            boardId: params.boardId,
            beginAt: params.beginAt,

            endAt: params.endAt,
            projectId: params.projectId,

            groupByProject: params.groupByProject,
            groupByMonthly: params.groupByMonthly,
            dateField: params.dateField,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/lv1/summary", option)
            .then((response) => {
              commit("SET_SUMMARY", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_SUMMARY_MONTHLY({ commit }, params) {
      return handleRequest('LOAD_SUMMARY_MONTHLY', params, () => {
        const option = {
          params: {
            projectId: params.projectId,
            boardId: params.boardId,
            beginYear: params.beginYear,
            endYear: params.endYear,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/lv1/summary/monthly", option)
            .then((response) => {
              commit("SET_SUMMARY", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_SUMMARY_GROUP_MONTHLY({ commit }, params) {
      return handleRequest('LOAD_SUMMARY_GROUP_MONTHLY', params, () => {
        //const group = ['all', 'project', 'company']
        const groupNameList = {
          3: "reportHealthAndSafetyManagementCost",
          45: "disasterClass",
          35: "hse_type",
          50: "agreementInfo",
          7: "disaster",
          41: "disaster",
          10: "workTypeB",
          11: "workTypeB",
          48: "checker",
          36: "disaster",
          40: "disaster",
        };
        const groupName = groupNameList[params.boardId];
        let beginAt = params.beginAt;
        let endAt = params.endAt;

        if (params.year) {
          beginAt = `${params.year}-01-01 00:00:00`;
          endAt = `${params.year}-12-31 23:59:59`;
        }

        const option = {
          params: {
            boardId: params.boardId,
            projectId: params.projectId,
            onlyMyCompany: params.onlyMyCompany,

            beginAt,
            endAt,

            groupByProject: 1,
            groupByMonthly: 1,
            groupByCompany: 1,
            dateField: "content$date",
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/lv1/summary/detail", option)
            .then((response) => {
              const summaryMonthly = createSummaryMonthly(response, groupName);
              if (params.projectId) {
                const projectName = Object.keys(summaryMonthly.sum.child)[0];
                commit("SET_SUMMARY_MONTHLY", {
                  sum: {
                    total: summaryMonthly.sum.child[projectName].total,
                    monthly: summaryMonthly.sum.child[projectName].monthly,
                    child: summaryMonthly.sum.child[projectName].child,
                  },
                  count: {
                    total: summaryMonthly.count.child[projectName].total,
                    monthly: summaryMonthly.count.child[projectName].monthly,
                    child: summaryMonthly.count.child[projectName].child,
                  },
                });
              } else {
                commit("SET_SUMMARY_MONTHLY", summaryMonthly);
              }
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    async LOAD_SUMMARY_GROUP_ITEMS({ commit }, params) {
      return handleRequest('LOAD_SUMMARY_GROUP_ITEMS', params, () => {
        //const group = ['all', 'project', 'company']
        const groupNameList = {
          3: "reportHealthAndSafetyManagementCost",
          45: "disasterClass",
          35: "hse_type",
          50: "agreementInfo",
          7: "disaster",
          41: "disaster",
          10: "workTypeB",
          11: "workTypeB",
          48: "checker",
          36: "disaster",
          40: "disaster",
        };

        const groupName = groupNameList[params.boardId];
        let beginAt = params.beginAt;
        let endAt = params.endAt;

        if (params.year) {
          beginAt = `${params.year}-01-01 00:00:00`;
          endAt = `${params.year}-12-31 23:59:59`;
        }

        const option = {
          params: {
            boardId: params.boardId,
            projectId: params.projectId,
            onlyMyCompany: params.onlyMyCompany,

            beginAt,
            endAt,

            groupByProject: 1,
            groupByMonthly: 1,
            groupByCompany: 1,
            dateField: "content$date",
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/summary/lv1/summary/detail", option)
            .then((response) => {
              const summaryItems = createSummaryItems(response, groupName);
              if (params.projectId) {
                const projectName = Object.keys(summaryItems.sum.child)[0];
                commit("SET_SUMMARY_ITEMS", {
                  sum: {
                    total: summaryItems.sum.child[projectName].total,
                    items: summaryItems.sum.child[projectName].items,
                    monthly: summaryItems.sum.child[projectName].monthly,
                    child: summaryItems.sum.child[projectName].child,
                  },
                  count: {
                    total: summaryItems.count.child[projectName].total,
                    items: summaryItems.count.child[projectName].items,
                    monthly: summaryItems.count.child[projectName].monthly,
                    child: summaryItems.count.child[projectName].child,
                  },
                });
              } else {
                commit("SET_SUMMARY_ITEMS", summaryItems);
              }
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    /**
     * 도넛차트02 에서 사용하는 종사자 정보 조회 API
     * @param params
     * @returns {Promise<unknown>}
     * @constructor
     */
    async LOAD_DONUT_EMPLOYEE(
      {
        /*commit*/
      },
      params
    ) {
      return handleRequest('LOAD_DONUT_EMPLOYEE', params, () => {
        const option = {
          params: {
            project_id: params.projectId,
            type: params.type,
            col: params.col,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/summary/employee`, option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    /**
     * 도넛차트02 자세히보기에서 사용하는 종사자 정보 조회 API
     * @param params
     * @returns {Promise<unknown>}
     * @constructor
     */
    async LOAD_DONUT_EMPLOYEE_DETAIL(
      {
        /*commit*/
      },
      params
    ) {
      return handleRequest('LOAD_DONUT_EMPLOYEE_DETAIL', params, () => {
        const option = {
          params: {
            project_id: params.projectId,
            current_year: params.currentYear,
            fieldIds: params.fieldIds,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/summary/employee/detail`, option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },

    /**
     * 분기별 종사자 현황
     * @param params
     * @returns {Promise<unknown>}
     * @constructor
     */
    async LOAD_SUMMARY_EMPLOYEE_QUARTER(
      {
        /*commit*/
      },
      params
    ) {
      return handleRequest('LOAD_SUMMARY_EMPLOYEE_QUARTER', params, () => {
        const option = {
          params: {
            project_id: params.projectId,
            target_year: params.targetYear,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/summary/employee/quarter`, option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    },
  },
  getters: {},
};
